import React from 'react';
import {
  currencyUI,
  currencySchema,
  radioUI,
  radioSchema,
  numberSchema,
  numberUI,
  textUI,
  textSchema,
  checkboxUI,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  currentOrPastDateRangeUI,
  currentOrPastDateRangeSchema,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  recipientTypeLabels,
  careTypeLabels,
  careFrequencyLabels,
} from '../../../utils/labels';
import { formatCurrency } from '../../../utils/helpers';
import ExpenseCard from '../../../components/ExpenseCard';
import {
  hideIfInHomeCare,
  requiredIfInHomeCare,
  transformDate,
} from './helpers';
import content from '../../../utils/content.json';

const nounSingular = 'care expense';
const nounPlural = 'care expenses';

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        Next we’ll ask you about unreimbursed recurring care expenses that you,
        your spouse, or your dependents pay for.
      </p>
      <p>
        Examples of unreimbursed care expenses include payments to in-home care
        providers, nursing homes, or other care facilities that insurance won’t
        cover.
      </p>
      <va-additional-info trigger="Additional documents you may need to submit">
        <p>
          If you’re reporting in-home care, nursing home, or other care facility
          expenses, you may need to submit proof for these expenses with your
          form.
        </p>
        <p>
          You may also need to submit 1 or more of these VA forms signed by a
          provider:
        </p>
        <ul>
          <li>
            Worksheet for a Residential Care, Adult Daycare, or Similar Facility
            from the PDF version of VA Form 21P-8416
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/find-forms/about-form-21p-8416/"
                text="Get VA Form 21P-8416 to download"
                external
              />
            </span>
          </li>
          <li>
            Worksheet for In-Home Attendant from the PDF version of VA Form
            21P-8416
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/find-forms/about-form-21p-8416/"
                text="Get VA Form 21P-8416 to download"
                external
              />
            </span>
          </li>
          <li>
            Request for Nursing Home Information in Connection with Claim for
            Aid and Attendance (VA Form 21-0779)
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/supporting-forms-for-claims/submit-nursing-home-information-form-21-0779"
                text="Submit nursing home information in connection with claim for Aid and Attendance"
                external
              />
            </span>
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/find-forms/about-form-21-0779/"
                text="Get VA Form 21-0779 to download"
                external
              />
            </span>
          </li>
          <li>
            Examination for Housebound Status or Permanent Need for Regular Aid
            and Attendance form (VA Form 21-2680)
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/pension/aid-attendance-housebound/apply-form-21-2680"
                text="Apply for Aid and Attendance benefits or Housebound allowance"
                external
              />
            </span>
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/find-forms/about-form-21-2680/"
                text="Get VA Form 21-2680 to download"
                external
              />
            </span>
          </li>
        </ul>
        <p>
          You can submit VA Form 21-0779 and VA Form 21-2680 online. All
          documents and worksheets can be uploaded at the end of this
          application or sent to us by mail.
        </p>
      </va-additional-info>
    </div>
  );
}

function checkIsItemIncomplete(item) {
  return (
    !item?.typeOfCare ||
    !item?.recipient ||
    ((item.recipient === 'CHILD' || item.recipient === 'OTHER') &&
      !item?.fullNameRecipient) ||
    !item?.provider ||
    !item?.careDate?.from ||
    !item?.monthlyAmount ||
    (item?.typeOfCare === 'IN_HOME_CARE_ATTENDANT' &&
      (!item?.hourlyRate || !item?.weeklyHours))
  );
}

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'careExpenses',
  nounSingular,
  nounPlural,
  required: false,
  isItemIncomplete: item => checkIsItemIncomplete(item),
  maxItems: 8,
  text: {
    getItemName: (item, index) =>
      replaceStrValues(content['array-item-name'], [
        index + 1,
        item?.provider || 'Care provider',
      ]),
    cardDescription: item => {
      if (!item?.careDate || !item?.monthlyAmount) return null; // this prevents console errors when navigating backwards when there are no items
      return (
        <ExpenseCard
          date={transformDate(item?.careDate?.from)}
          endDate={transformDate(item?.careDate?.to)}
          frequency={careFrequencyLabels?.ONCE_MONTH}
          amount={formatCurrency(item?.monthlyAmount)}
        />
      );
    },
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      nounSingular,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      nounSingular,
    ),
    cancelAddDescription: replaceStrValues(
      content['array-cancel-add-description'],
      [nounPlural, nounSingular],
    ),
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      nounPlural,
      nounSingular,
    ]),
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(content['array-delete-title'], nounSingular),
    deleteYes: content['array-delete-yes'],
  },
};

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['care-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/**
 * This page is skipped on the first loop for required flow
 * Cards are populated on this page above the uiSchema if items are present
 *
 * @returns {PageSchema}
 */
const summaryPage = {
  uiSchema: {
    'view:careExpensesList': arrayBuilderYesNoUI(
      options,
      { hint: '' },
      { hint: '' },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:careExpensesList': arrayBuilderYesNoSchema,
    },
    required: ['view:careExpensesList'],
  },
};

/** @returns {PageSchema} */
const typeOfCarePage = {
  uiSchema: {
    typeOfCare: radioUI({
      title: content['care-type-title'],
      labelHeaderLevel: 3,
      labels: careTypeLabels,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      typeOfCare: radioSchema(Object.keys(careTypeLabels)),
    },
    required: ['typeOfCare'],
  },
};

/** @returns {PageSchema} */
const recipientPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['care-recipient-page-title'],
    ),
    recipient: radioUI({
      title: content['care-recipient-question'],
      labels: recipientTypeLabels,
    }),
    fullNameRecipient: textUI({
      title: content['care-recipient-fullname'],
      expandUnder: 'recipient',
      expandUnderCondition: field => field === 'CHILD' || field === 'OTHER',
      required: (formData, index, fullData) => {
        // Adding a check for formData and fullData since formData is sometimes undefined on load
        // and we can't rely on fullData for testing
        const careExpenses = formData.careExpenses ?? fullData.careExpenses;
        const careExpense = careExpenses?.[index];
        return ['CHILD', 'OTHER'].includes(careExpense?.recipient);
      },
    }),
    provider: textUI(content['care-provider-question']),
  },
  schema: {
    type: 'object',
    properties: {
      recipient: radioSchema(Object.keys(recipientTypeLabels)),
      fullNameRecipient: textSchema,
      provider: textSchema,
    },
    required: ['recipient', 'provider'],
  },
};
/** @returns {PageSchema} */
const datePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(content['care-dates-page-title']),
    careDate: currentOrPastDateRangeUI(
      {
        title: content['care-start-date'],
        hint: content['care-date-hint'],
        removeDateHint: true,
        monthSelect: false,
      },
      {
        title: content['care-end-date'],
        hideIf: (formData, index, fullData) => {
          const careExpenses = formData?.careExpenses ?? fullData?.careExpenses;
          const careExpense = careExpenses?.[index];
          return careExpense?.noCareEndDate === true;
        },
        removeDateHint: true,
        monthSelect: false,
      },
    ),
    noCareEndDate: checkboxUI(content['care-no-end-date']),
  },
  schema: {
    type: 'object',
    properties: {
      careDate: {
        ...currentOrPastDateRangeSchema,
        required: ['from'],
      },
      noCareEndDate: {
        type: 'boolean',
      },
    },
    required: ['typeOfCare'],
  },
};

/** @returns {PageSchema} */
const costPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(content['care-cost-page-title']),
    monthlyAmount: currencyUI({
      title: content['care-monthly-amount'],
      max: 999999.99,
    }),
    hourlyRate: {
      ...currencyUI({
        title: content['care-hourly-rate'],
        hideIf: (formData, index, fullData) =>
          hideIfInHomeCare(formData, index, fullData),
        max: 999,
      }),
      'ui:required': (formData, index, fullData) =>
        requiredIfInHomeCare(formData, index, fullData),
    },
    weeklyHours: {
      ...numberUI({
        title: content['care-weekly-hours'],
        hint: content['care-weekly-hours-hint'],
        hideIf: (formData, index, fullData) =>
          hideIfInHomeCare(formData, index, fullData),
        max: 999,
      }),
      'ui:required': (formData, index, fullData) =>
        requiredIfInHomeCare(formData, index, fullData),
    },
  },
  schema: {
    type: 'object',
    properties: {
      monthlyAmount: currencySchema,
      hourlyRate: currencySchema,
      weeklyHours: numberSchema,
    },
    required: ['monthlyAmount'],
  },
};

export const careExpensesPages = arrayBuilderPages(options, pageBuilder => ({
  careExpensesIntro: pageBuilder.introPage({
    title: content['care-intro-title'],
    path: 'expenses/care',
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  careExpensesSummary: pageBuilder.summaryPage({
    title: content['care-intro-title'],
    path: 'expenses/care/add',
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  careExpensesTypePage: pageBuilder.itemPage({
    title: content['care-type-title'],
    path: 'expenses/care/:index/type-of-care',
    uiSchema: typeOfCarePage.uiSchema,
    schema: typeOfCarePage.schema,
  }),
  careExpensesRecipientPage: pageBuilder.itemPage({
    title: content['care-recipient-page-title'],
    path: 'expenses/care/:index/recipient',
    uiSchema: recipientPage.uiSchema,
    schema: recipientPage.schema,
  }),
  careExpensesDatesPage: pageBuilder.itemPage({
    title: content['care-dates-page-title'],
    path: 'expenses/care/:index/dates',
    uiSchema: datePage.uiSchema,
    schema: datePage.schema,
  }),
  careExpensesCostPage: pageBuilder.itemPage({
    title: content['care-cost-page-title'],
    path: 'expenses/care/:index/cost',
    uiSchema: costPage.uiSchema,
    schema: costPage.schema,
  }),
}));
